import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  LEADER_RELEASE_CONFIG,
  leaderPageText,
  LeaderPageLanguage,
} from '../services/leaderPageText';
import GetData from '../services/GetData';
import { fileUrl, requests } from '../services/Requests';
import Top100LeadersCollage1 from '../styles/images/fwtop100/Top100Leaders_Collage_1.png';
import Top100LeadersCollage2 from '../styles/images/fwtop100/Top100Leaders_Collage_2.png';

type LeaderCard = {
  id: number;
  name: string;
  nameArabic: string;
  position: string;
  positionArabic: string;
  company?: string;
  companyArabic?: string;
  imageUrl?: string;
  sector: string;
  sectorArabic: string;
  biography: string;
  biographyArabic: string;
  order: number;
};

const collageImages = [
  Top100LeadersCollage1,
  Top100LeadersCollage2,
];

type PopupImageFit = 'unknown' | 'portrait' | 'landscape' | 'square';
const UAE_TIME_ZONE = 'Asia/Dubai';
const UAE_OFFSET_HOURS = 4;

const cleanImageValue = (value?: string) => {
  let cleaned = String(value || '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim();

  // Some payloads come wrapped like ["https://..."] or "https://..."
  cleaned = cleaned.replace(/^\[\s*["']?/, '').replace(/["']?\s*\]$/, '');
  cleaned = cleaned.replace(/^["']+|["']+$/g, '').trim();

  return cleaned;
};

const extractImageUrlCandidate = (value: string) => {
  if (!value) return '';

  // Try to unwrap serialized payloads like ["https://..."] or "\"https://...\"".
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return cleanImageValue(String(parsed[0]));
    }
    if (typeof parsed === 'string') {
      return cleanImageValue(parsed);
    }
  } catch {
    // fall through to regex extraction
  }

  const match = value.match(/https?:\/\/[^"'\\\]]+/i);
  return cleanImageValue(match ? match[0] : value);
};

const normalizeImageUrl = (value?: string) => {
  const cleaned = cleanImageValue(value);
  if (!cleaned) return '';
  const candidate = extractImageUrlCandidate(cleaned);

  try {
    return new URL(candidate).toString();
  } catch {
    try {
      return new URL(encodeURI(candidate)).toString();
    } catch {
      return '';
    }
  }
};

const getLeaderImageUrl = (imageUrl?: string, imagePath?: string) => {
  const directUrl = normalizeImageUrl(imageUrl);
  if (directUrl) return directUrl;

  const path = cleanImageValue(imagePath);
  if (!path) return '';

  if (/^https?:\/\//i.test(path)) {
    return normalizeImageUrl(path);
  }

  return normalizeImageUrl(`${fileUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`);
};

const normalizeSectorName = (sector?: string, sectorArabic?: string) => {
  const normalizedEn = String(sector || '').trim();
  const normalizedAr = String(sectorArabic || '').trim();

  const enTourismVariants = [
    'Tourism & Hospitality',
    'Tourism and Hospitality',
    'tourism & hospitality',
    'tourism and hospitality',
  ];

  const arTourismVariants = ['السياحة والضيافة'];

  const isTourismSector =
    enTourismVariants.includes(normalizedEn) || arTourismVariants.includes(normalizedAr);

  if (!isTourismSector) {
    return {
      sector: normalizedEn,
      sectorArabic: normalizedAr,
    };
  }

  return {
    sector: 'Aviation & Transport',
    sectorArabic: 'الطيران والنقل',
  };
};

const dummyLeaders: LeaderCard[] = [
  {
    id: 101,
    name: 'HH Sheikh Ahmed bin Saeed Al Maktoum',
    nameArabic: 'سمو الشيخ أحمد بن سعيد آل مكتوم',
    position: 'Chairman & Chief Executive, Emirates Airline & Group',
    positionArabic: 'رئيس مجلس الإدارة والرئيس التنفيذي، طيران الإمارات والمجموعة',
    sector: 'Logistics & Trade',
    sectorArabic: 'الخدمات اللوجستية والتجارة',
    biography:
      'HH Sheikh Ahmed bin Saeed Al Maktoum began his aviation career in 1985, when he was appointed President of the Dubai Department of Civil Aviation. In the same year, he became Chairman of the newly founded Emirates and led the airline launch with two leased aircraft serving two destinations from Dubai. Today, he leads the Emirates Group, including dnata. Emirates is the world’s largest international airline, serving 152 destinations across six continents and operating 281 aircraft, including the world’s largest Boeing 777 and Airbus A380 fleets, in addition to recently inducted Airbus A350 aircraft. dnata is one of the world’s largest air services providers, offering ground handling, cargo, travel and flight catering services across six continents and supporting more than 300 airline customers globally. Sheikh Ahmed has played a critical role in strengthening Dubai’s position as a global hub for business, tourism, trade and transport. Through multiple government leadership roles, he has contributed to policies and strategies that advance Dubai’s vision, economy, communities and culture. He also supports many charitable organizations and has received prestigious national and international honors. He holds a bachelor’s degree from the University of Denver, Colorado, USA. The Royal Aeronautical Society recognized his aviation achievements with a fellowship at the 1994 Farnborough Air Show in the UK, and in May 2013 he received an honorary Doctor of Science degree from City University London – Cass Business School.',
    biographyArabic:
      'بدأ سمو الشيخ أحمد بن سعيد آل مكتوم مسيرته في قطاع الطيران عام 1985 عندما عُيّن رئيسًا لدائرة الطيران المدني في دبي. وفي العام نفسه تولّى رئاسة مجلس إدارة طيران الإمارات عند تأسيسها، وقاد انطلاقتها بطائرتين مستأجرتين إلى وجهتين فقط من دبي. ويقود اليوم مجموعة الإمارات التي تضم dnata. تُعد طيران الإمارات أكبر ناقل دولي في العالم، وتخدم 152 وجهة عبر ست قارات، بأسطول يضم 281 طائرة، بما في ذلك أكبر أسطول عالمي من طائرات بوينغ 777 وإيرباص A380، إضافة إلى طائرات إيرباص A350 المنضمة حديثًا. وتُعد dnata من أكبر مزودي الخدمات الجوية عالميًا، حيث تقدم خدمات المناولة الأرضية والشحن والسفر والتموين الجوي عبر ست قارات، وتدعم أكثر من 300 شركة طيران حول العالم. وقد أسهم سموه بشكل كبير في ترسيخ مكانة دبي كمركز عالمي للأعمال والسياحة والتجارة والنقل، كما لعب عبر مناصبه الحكومية دورًا مهمًا في صياغة سياسات واستراتيجيات دعمت رؤية دبي واقتصادها ومجتمعها وثقافتها. وهو كذلك راعٍ لعدد من المبادرات الإنسانية والخيرية، وحاصل على أوسمة وتكريمات وطنية ودولية مرموقة. يحمل سموه درجة البكالوريوس من جامعة دنفر في كولورادو بالولايات المتحدة، كما منحته الجمعية الملكية للطيران زمالة خلال معرض فارنبورو للطيران عام 1994 في المملكة المتحدة، ونال في مايو 2013 درجة الدكتوراه الفخرية في العلوم من جامعة سيتي لندن – كلية كاس للأعمال.',
    order: 1,
  },
  {
    id: 1,
    name: 'Saeed Al Tayer',
    nameArabic: 'سعيد الطاير',
    position: 'Vice Chairman & MD, DEWA',
    positionArabic: 'نائب الرئيس والعضو المنتدب، ديوا',
    sector: 'Energy & Utilities',
    sectorArabic: 'الطاقة والمرافق',
    biography:
      'A long-serving executive known for driving strategic infrastructure and utility transformation in the region.',
    biographyArabic:
      'قيادي بارز يقود التحول الاستراتيجي في مشاريع البنية التحتية والطاقة والمرافق في المنطقة.',
    order: 2,
  },
  {
    id: 2,
    name: 'Tareq Al Ashban',
    nameArabic: 'طارق الأشبان',
    position: 'Chief Executive Officer',
    positionArabic: 'الرئيس التنفيذي',
    sector: 'Banking & Capital Markets',
    sectorArabic: 'البنوك وأسواق رأس المال',
    biography:
      'Leads growth and governance initiatives across institutional banking and capital market expansion.',
    biographyArabic:
      'يقود مبادرات النمو والحوكمة في الخدمات المصرفية المؤسسية وتوسّع أسواق رأس المال.',
    order: 3,
  },
  {
    id: 3,
    name: 'Alison Rehill-Erguven',
    nameArabic: 'أليسون ريهيل إرغوفين',
    position: 'Chief Executive Officer',
    positionArabic: 'الرئيس التنفيذي',
    sector: 'Retail & Consumer',
    sectorArabic: 'التجزئة والسلع الاستهلاكية',
    biography:
      'Recognised for consumer growth strategy, category innovation and market-wide talent development.',
    biographyArabic:
      'معروفة باستراتيجية نمو المستهلك والابتكار في الفئات وتطوير المواهب على مستوى السوق.',
    order: 4,
  },
  {
    id: 4,
    name: 'Ahmad Aldebasi',
    nameArabic: 'أحمد الدباسي',
    position: 'General Manager',
    positionArabic: 'مدير عام',
    sector: 'Real Estate',
    sectorArabic: 'العقارات',
    biography:
      'Drives high-impact real estate investments and long-term value creation through disciplined execution.',
    biographyArabic:
      'يقود استثمارات عقارية عالية الأثر ويعزز خلق القيمة طويلة الأمد عبر تنفيذ منضبط.',
    order: 5,
  },
  {
    id: 5,
    name: 'Fahad Al Hassawi',
    nameArabic: 'فهد الحساوي',
    position: 'Chief Executive Officer',
    positionArabic: 'الرئيس التنفيذي',
    sector: 'Telecom & Media',
    sectorArabic: 'الاتصالات والإعلام',
    biography:
      'Steers digital transformation, customer innovation and resilient connectivity programs.',
    biographyArabic:
      'يقود التحول الرقمي وابتكار تجربة العملاء وبرامج الاتصال المرنة.',
    order: 6,
  },
  {
    id: 6,
    name: 'Khalid Alghafli',
    nameArabic: 'خالد الغافلي',
    position: 'President',
    positionArabic: 'الرئيس',
    sector: 'Logistics & Trade',
    sectorArabic: 'الخدمات اللوجستية والتجارة',
    biography:
      'Focused on network efficiency, cross-border trade and supply-chain modernization.',
    biographyArabic:
      'يركز على كفاءة الشبكات والتجارة العابرة للحدود وتحديث سلاسل الإمداد.',
    order: 7,
  },
];

const sectorKeys = [
  'banking',
  'energy',
  'ai',
  'real-estate',
  'health',
  'retail',
  'logistics',
  'telecom',
  'industrial',
  'tourism',
  'fintech',
] as const;

const MARQUEE_REPEAT_COUNT = 6;
const PRIORITY_IMAGE_COUNT = 8;

const getTimeLeft = (targetDate: Date) => {
  const distance = targetDate.getTime() - new Date().getTime();
  if (distance <= 0) {
    return { days: '00', hours: '00', minutes: '00', seconds: '00' };
  }

  const days = Math.floor(distance / (1000 * 60 * 60 * 24));
  const hours = Math.floor((distance / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((distance / (1000 * 60)) % 60);
  const seconds = Math.floor((distance / 1000) % 60);

  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
  };
};

const getCurrentUaeYear = () => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: UAE_TIME_ZONE,
    year: 'numeric',
  });

  return Number.parseInt(formatter.format(new Date()), 10);
};

const parseReleaseDateWithUaeFallback = (value?: string) => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;

  // If backend sends explicit timezone, trust that absolute timestamp.
  if (/[zZ]|[+\-]\d{2}:?\d{2}$/.test(normalized)) {
    const direct = new Date(normalized);
    return Number.isNaN(direct.getTime()) ? null : direct;
  }

  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2})(?::(\d{2}))?(?::(\d{2}))?)?$/,
  );
  if (!match) {
    const direct = new Date(normalized);
    return Number.isNaN(direct.getTime()) ? null : direct;
  }

  const [, yearRaw, monthRaw, dayRaw, hourRaw = '00', minuteRaw = '00', secondRaw = '00'] = match;
  const year = Number.parseInt(yearRaw, 10);
  const month = Number.parseInt(monthRaw, 10) - 1;
  const day = Number.parseInt(dayRaw, 10);
  const hour = Number.parseInt(hourRaw, 10);
  const minute = Number.parseInt(minuteRaw, 10);
  const second = Number.parseInt(secondRaw, 10);

  if ([year, month, day, hour, minute, second].some((part) => Number.isNaN(part))) {
    return null;
  }

  // Treat timezone-less API datetime as UAE local time.
  return new Date(Date.UTC(year, month, day, hour - UAE_OFFSET_HOURS, minute, second, 0));
};

const getUaeFallbackReleaseDate = () => {
  const uaeYear = getCurrentUaeYear();
  return new Date(
    Date.UTC(
      uaeYear,
      LEADER_RELEASE_CONFIG.releaseDate.monthIndex,
      LEADER_RELEASE_CONFIG.releaseDate.day,
      -UAE_OFFSET_HOURS,
      0,
      0,
      0,
    ),
  );
};

const renderAccentText = (text: string): ReactNode => {
  const chunks = text.split(/(<accent>.*?<\/accent>)/g).filter(Boolean);
  return chunks.map((chunk, index) => {
    const match = chunk.match(/^<accent>(.*?)<\/accent>$/);
    if (match) {
      return (
        <span key={`accent-${index}`} className="fwtop100-accent">
          {match[1]}
        </span>
      );
    }
    return <span key={`text-${index}`}>{chunk}</span>;
  });
};

const ROLL_REPEATS = 3;
const ROLL_BASE_INDEX = 10;
const ROLL_UNIT_STAGGER_MS = 108;
const ROLL_DIGIT_STAGGER_MS = 32;
const ROLL_BASE_DURATION_MS = 1035;
const ROLL_INTRO_DURATION_MS = 1800;
const ROLL_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const ROLL_INTRO_EASING = 'linear';
const ROLL_STRIP_DIGITS = Array.from({ length: ROLL_REPEATS * 10 }, (_, index) =>
  String(index % 10),
);
const STATIC_REEL_STRIP_DIGITS = Array.from({ length: 30 }, (_, index) => String(index % 10));
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
const toTwoDigit = (value: string) => String(Math.max(0, Number.parseInt(value, 10) || 0)).padStart(2, '0').slice(-2);
const useInViewPulse = (threshold = 0.35) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pulse, setPulse] = useState(0);
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (typeof IntersectionObserver === 'undefined') {
      if (!hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        setPulse((prev) => prev + 1);
      }
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTriggeredRef.current) {
            hasTriggeredRef.current = true;
            setPulse((prev) => prev + 1);
            observer.disconnect();
          }
        });
      },
      { threshold },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, pulse };
};

const getRollTiming = (unitOrder: number, digitOrder: number) => {
  // Seconds update every 1s, so keep this unit faster to avoid perpetual blur overlap.
  if (unitOrder === 3) {
    return {
      delay: digitOrder * 18,
      duration: 420,
      introDuration: 900,
    };
  }

  return {
    delay: unitOrder * ROLL_UNIT_STAGGER_MS + digitOrder * ROLL_DIGIT_STAGGER_MS,
    duration: ROLL_BASE_DURATION_MS,
    introDuration: ROLL_INTRO_DURATION_MS,
  };
};

const StaticReelDigit = ({
  digit,
  order,
  trigger,
}: {
  digit: string;
  order: number;
  trigger: number;
}) => {
  const stripRef = useRef<HTMLSpanElement | null>(null);
  const currentIndexRef = useRef(19);
  const previousDigitRef = useRef<number | null>(null);
  const lastTriggerRef = useRef<number>(0);
  const mountedRef = useRef(false);
  const spinTokenRef = useRef(0);

  useIsomorphicLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;

    const nextDigit = Number.parseInt(digit, 10);
    if (!Number.isFinite(nextDigit)) return;

    const toTranslate = (index: number) => `translateY(${-index}em)`;
    const token = ++spinTokenRef.current;
    const delay = order * 44;
    const duration = mountedRef.current ? 860 : 1200;
    const shouldReplay = lastTriggerRef.current !== trigger;
    lastTriggerRef.current = trigger;

    if (mountedRef.current && previousDigitRef.current === nextDigit && !shouldReplay) {
      return;
    }

    if (!mountedRef.current || shouldReplay) {
      mountedRef.current = true;
      strip.style.transition = 'none';
      strip.style.transform = toTranslate(19);
      currentIndexRef.current = 19;
      previousDigitRef.current = 9;
      void strip.offsetHeight;
    }

    const fromIndex = currentIndexRef.current;
    let toIndex = 10 + nextDigit;
    while (toIndex >= fromIndex) toIndex -= 10;

    strip.classList.add('fwtop100-reel-digit-strip--spinning');
    strip.style.transition = `transform ${duration}ms ${ROLL_EASING} ${delay}ms`;
    strip.style.transform = toTranslate(toIndex);
    currentIndexRef.current = toIndex;
    previousDigitRef.current = nextDigit;

    const settleTimeout = window.setTimeout(() => {
      if (spinTokenRef.current !== token) return;
      const settledIndex = 10 + nextDigit;
      currentIndexRef.current = settledIndex;
      strip.classList.remove('fwtop100-reel-digit-strip--spinning');
      strip.style.transition = 'none';
      strip.style.transform = toTranslate(settledIndex);
    }, delay + duration + 50);

    return () => {
      window.clearTimeout(settleTimeout);
      strip.classList.remove('fwtop100-reel-digit-strip--spinning');
    };
  }, [digit, order, trigger]);

  return (
    <span className="fwtop100-reel-digit-window" aria-hidden="true">
      <span ref={stripRef} className="fwtop100-reel-digit-strip">
        {STATIC_REEL_STRIP_DIGITS.map((value, index) => (
          <span key={`${value}-${index}`} className="fwtop100-reel-digit-char">
            {value}
          </span>
        ))}
      </span>
    </span>
  );
};

const ReelInlineValue = ({
  value,
  rtl,
  trigger,
}: {
  value: string;
  rtl: boolean;
  trigger: number;
}) => {
  const renderAnimatedValue = (input: string, isRtl: boolean) => {
    const chars = Array.from(input);
    const digitPositions = chars
      .map((char, index) => (/\d/.test(char) ? index : -1))
      .filter((index) => index >= 0);
    const digitCount = digitPositions.length;
    let seenDigits = 0;

    return chars.map((char, index) => {
      const isDigit = /\d/.test(char);
      if (!isDigit) {
        const staticClasses = ['fwtop100-reel-static-char'];
        if (/\s/.test(char)) staticClasses.push('fwtop100-reel-static-char--space');
        if (/[$€£]/.test(char)) staticClasses.push('fwtop100-reel-static-char--currency');
        if (/[\u0600-\u06FFA-Za-z]/.test(char)) staticClasses.push('fwtop100-reel-static-char--letter');

        return (
          <span key={`char-${index}-${char}`} className={staticClasses.join(' ')} aria-hidden="true">
            {char}
          </span>
        );
      }

      const order = isRtl ? digitCount - 1 - seenDigits : seenDigits;
      seenDigits += 1;

      return <StaticReelDigit key={`digit-${index}-${char}`} digit={char} order={order} trigger={trigger} />;
    });
  };

  const hasArabic = /[\u0600-\u06FF]/.test(value);
  const hasCurrency = /[$€£]/.test(value);
  const hasDigit = /\d/.test(value);
  const shouldUseRtlMixedLayout = rtl && hasArabic && hasCurrency && hasDigit;

  if (shouldUseRtlMixedLayout) {
    const numberPart = (value.match(/\d[\d.,]*/) || [''])[0];
    const currencyPart = (value.match(/[$€£]+/) || [''])[0];
    const textPart = value
      .replace(numberPart, '')
      .replace(/[$€£]+/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    return (
      <span className="fwtop100-reel-inline fwtop100-reel-inline--rtl-mixed" aria-label={value}>
        {currencyPart ? (
          <span className="fwtop100-reel-static-char fwtop100-reel-static-char--currency" aria-hidden="true">
            {currencyPart}
          </span>
        ) : null}
        {textPart ? (
          <span
            className="fwtop100-reel-static-char fwtop100-reel-static-char--letter fwtop100-reel-arabic-segment"
            dir="rtl"
            aria-hidden="true"
          >
            {textPart}
          </span>
        ) : null}
        <span className="fwtop100-reel-inline-number" aria-hidden="true">
          {renderAnimatedValue(numberPart, false)}
        </span>
      </span>
    );
  }

  return (
    <span className="fwtop100-reel-inline" aria-label={value}>
      {renderAnimatedValue(value, rtl)}
    </span>
  );
};

const ReelDigit = ({
  digit,
  unitOrder,
  digitOrder,
}: {
  digit: string;
  unitOrder: number;
  digitOrder: number;
}) => {
  const stripRef = useRef<HTMLSpanElement | null>(null);
  const currentIndexRef = useRef(ROLL_BASE_INDEX + 9);
  const mountedRef = useRef(false);
  const spinTokenRef = useRef(0);

  useIsomorphicLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;

    const nextDigit = Number.parseInt(digit, 10) || 0;
    const stepHeight = strip.parentElement?.clientHeight || 36;
    const toTranslate = (index: number) => `translateY(${-index * stepHeight}px)`;
    const { delay, duration, introDuration } = getRollTiming(unitOrder, digitOrder);
    const token = ++spinTokenRef.current;

    if (!mountedRef.current) {
      mountedRef.current = true;
      const fromIndex = ROLL_BASE_INDEX + 9;
      let toIndex = ROLL_BASE_INDEX + nextDigit;
      while (toIndex >= fromIndex) toIndex -= 10;

      strip.style.transition = 'none';
      strip.style.transform = toTranslate(fromIndex);
      currentIndexRef.current = toIndex;
      void strip.offsetHeight;

      strip.style.transition = `transform ${introDuration}ms ${ROLL_INTRO_EASING} ${delay}ms`;
      strip.style.transform = toTranslate(toIndex);

      const settleTimeout = window.setTimeout(() => {
        if (spinTokenRef.current !== token) return;
        const settledIndex = ROLL_BASE_INDEX + nextDigit;
        currentIndexRef.current = settledIndex;
        strip.style.transition = 'none';
        strip.style.transform = toTranslate(settledIndex);
      }, delay + introDuration + 50);

      return () => {
        window.clearTimeout(settleTimeout);
        strip.classList.remove('fwtop100-roll-strip--spinning');
      };
    }

    const fromIndex = currentIndexRef.current;
    let toIndex = ROLL_BASE_INDEX + nextDigit;
    while (toIndex >= fromIndex) toIndex -= 10;

    strip.classList.remove('fwtop100-roll-strip--spinning');
    strip.style.transition = 'none';
    strip.style.transform = toTranslate(fromIndex);
    currentIndexRef.current = toIndex;
    void strip.offsetHeight;

    strip.style.transition = `transform ${duration}ms ${ROLL_EASING} ${delay}ms`;
    strip.style.transform = toTranslate(toIndex);

    const settleTimeout = window.setTimeout(() => {
      if (spinTokenRef.current !== token) return;
      const settledIndex = ROLL_BASE_INDEX + nextDigit;
      currentIndexRef.current = settledIndex;
      strip.classList.remove('fwtop100-roll-strip--spinning');
      strip.style.transition = 'none';
      strip.style.transform = toTranslate(settledIndex);
    }, delay + duration + 50);

    return () => {
      window.clearTimeout(settleTimeout);
      strip.classList.remove('fwtop100-roll-strip--spinning');
    };
  }, [digit, unitOrder, digitOrder]);

  return (
    <span className="fwtop100-roll-char" aria-hidden="true">
      <span ref={stripRef} className="fwtop100-roll-strip">
        {ROLL_STRIP_DIGITS.map((value, index) => (
          <span key={`${value}-${index}`} className="fwtop100-roll-number fwtop100-roll-value">
            {value}
          </span>
        ))}
      </span>
    </span>
  );
};

const renderRollDigits = (value: string, unit: string, unitOrder: number, rtl: boolean) => {
  const digitsOnly = value.replace(/[^\d]/g, '');
  const isDayUnit = unit === 'DAYS' || unit === 'يوماً';
  const normalized = isDayUnit ? digitsOnly || '0' : (digitsOnly || '0').slice(-2).padStart(2, '0');

  return (
    <div className="fwtop100-roll-unit" key={unit}>
      <span className="fwtop100-roll-slot" aria-live="polite">
        {normalized.split('').map((digit, digitIndex) => (
          <ReelDigit
            key={`${unit}-${digitIndex}`}
            digit={digit}
            unitOrder={unitOrder}
            digitOrder={rtl ? normalized.length - 1 - digitIndex : digitIndex}
          />
        ))}
      </span>
      <small>{unit}</small>
    </div>
  );
};

const FwTop100Page = () => {
  const fallbackReleaseDate = useMemo(() => getUaeFallbackReleaseDate(), []);
  const [releaseDate, setReleaseDate] = useState<Date>(fallbackReleaseDate);
  const [language, setLanguage] = useState<LeaderPageLanguage>('ar');
  const [leaders, setLeaders] = useState<LeaderCard[]>(dummyLeaders);
  const [isLeadersLoading, setIsLeadersLoading] = useState<boolean>(false);
  const [leadersError, setLeadersError] = useState<string>('');
  const [failedLeaderImages, setFailedLeaderImages] = useState<Record<number, boolean>>({});
  const [isPopupImageFailed, setIsPopupImageFailed] = useState<boolean>(false);
  const [activeSector, setActiveSector] = useState<string>('all');
  const [selectedLeader, setSelectedLeader] = useState<LeaderCard | null>(null);
  const [popupImageFit, setPopupImageFit] = useState<PopupImageFit>('unknown');
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(releaseDate));
  const [isListUnlocked, setIsListUnlocked] = useState<boolean>(
    Date.now() >= fallbackReleaseDate.getTime(),
  );
  const warmedPopupImageUrlsRef = useRef<Set<string>>(new Set());
  const hasFetchedLeadersRef = useRef(false);
  const { ref: heroStatsRef, pulse: heroStatsPulse } = useInViewPulse(0.45);
  const { ref: metricsRef, pulse: metricsPulse } = useInViewPulse(0.35);
  const [activeCollage, setActiveCollage] = useState(0);

  const content = leaderPageText[language];
  const dayCount = Math.max(0, Number.parseInt(timeLeft.days, 10) || 0);
  const dayDisplay = String(dayCount);
  const isArabic = language === 'ar';

  const filterItems = useMemo(() => {
    const sectorSet = new Set<string>(
      leaders
        .map((leader) => {
          const primary = isArabic ? leader.sectorArabic : leader.sector;
          const fallback = isArabic ? leader.sector : leader.sectorArabic;
          return String(primary || fallback || '').trim();
        })
        .filter(Boolean),
    );
    return ['all', ...Array.from(sectorSet)];
  }, [leaders, isArabic]);

  const filteredLeaders = useMemo(() => {
    if (activeSector === 'all') {
      return leaders;
    }
    return leaders.filter((leader) => {
      const primary = isArabic ? leader.sectorArabic : leader.sector;
      const fallback = isArabic ? leader.sector : leader.sectorArabic;
      return String(primary || fallback || '').trim() === activeSector;
    });
  }, [activeSector, leaders, isArabic]);

  const warmPopupImage = (url?: string) => {
    if (typeof window === 'undefined') return;
    const normalized = String(url || '').trim();
    if (!normalized) return;
    if (warmedPopupImageUrlsRef.current.has(normalized)) return;

    warmedPopupImageUrlsRef.current.add(normalized);
    const img = new window.Image();
    img.decoding = 'async';
    img.src = normalized;
    if (typeof img.decode === 'function') {
      img.decode().catch(() => undefined);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveCollage((prev) => (prev + 1) % collageImages.length);
    }, 5000);
    // console.log(Top100LeadersCollage1);
    // console.log(Top100LeadersCollage2);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchReleaseDate = async () => {
      const result = await GetData(
        `${requests.leaders}/release-on`,
        {},
        'get',
        false,
      );

      const releaseOn = result?.data?.response?.releaseOn;
      const parsedReleaseDate = parseReleaseDateWithUaeFallback(releaseOn);

      if (parsedReleaseDate && !Number.isNaN(parsedReleaseDate.getTime())) {
        setReleaseDate(parsedReleaseDate);
      }
    };

    fetchReleaseDate();
  }, []);

  useEffect(() => {
    if (!isListUnlocked) {
      return;
    }

    if (hasFetchedLeadersRef.current) {
      return;
    }

    const fetchLeaders = async () => {
      setIsLeadersLoading(true);
      setLeadersError('');

      const result = await GetData(
        `${requests.leaders}/getAll`,
        { limit: 100, pageNo: 1 },
        'get',
        false,
      );

      if (result?.error) {
        setLeadersError(result?.error?.message || 'Failed to fetch leaders.');
        setIsLeadersLoading(false);
        return;
      }

      const leadersResponse = result?.data?.response?.leaders;
      if (!Array.isArray(leadersResponse)) {
        setLeadersError('Invalid leaders response.');
        setIsLeadersLoading(false);
        return;
      }

      const mappedLeaders: LeaderCard[] = leadersResponse.map((leader: any) => {
        const normalizedSector = normalizeSectorName(leader?.sector, leader?.sectorArabic);
        return {
          id: Number(leader?.id) || 0,
          name: leader?.name || '',
          nameArabic: leader?.nameArabic || '',
          position: leader?.position || '',
          positionArabic: leader?.positionArabic || '',
          company: leader?.company || '',
          companyArabic: leader?.companyArabic || '',
          imageUrl: getLeaderImageUrl(leader?.imageUrl, leader?.image?.path),
          sector: normalizedSector.sector,
          sectorArabic: normalizedSector.sectorArabic,
          biography: leader?.biography || '',
          biographyArabic: leader?.biographyArabic || '',
          order: Number(leader?.order) || 0,
        };
      });

      mappedLeaders.sort((a, b) => a.order - b.order);
      setLeaders(mappedLeaders);
      hasFetchedLeadersRef.current = true;
      setIsLeadersLoading(false);
    };

    fetchLeaders();
  }, [isListUnlocked]);

  useEffect(() => {
    if (activeSector === 'all') return;
    const hasActiveSector = filterItems.includes(activeSector);
    if (!hasActiveSector) {
      setActiveSector('all');
    }
  }, [activeSector, filterItems]);

  useEffect(() => {
    setIsPopupImageFailed(false);
    setPopupImageFit('unknown');
  }, [selectedLeader?.id]);

  useEffect(() => {
    setTimeLeft(getTimeLeft(releaseDate));
    // setIsListUnlocked(Date.now() >= releaseDate.getTime());
    setIsListUnlocked(true); // Temporarily unlock the list for testing purposes

    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft(releaseDate));
      // setIsListUnlocked(Date.now() >= releaseDate.getTime());
      setIsListUnlocked(true);
    }, 1000);

    return () => clearInterval(interval);
  }, [releaseDate]);

  const activeRollClock = {
    hours: toTwoDigit(timeLeft.hours),
    minutes: toTwoDigit(timeLeft.minutes),
    seconds: toTwoDigit(timeLeft.seconds),
  };
  const activeDayDisplay = dayDisplay;

  useEffect(() => {
    document.body.classList.add('fwtop100-body');
    console.log(collageImages);
    return () => {
      document.body.classList.remove('fwtop100-body');
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (!section) return;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Head>
        <title>{language === 'ar' ? 'أفضل 100 قائد' : 'Top 100 Leaders'}</title>
      </Head>
      <main
        className={`fwtop100-page ${language === 'ar' ? 'fwtop100-page--ar' : 'fwtop100-page--en'
          }`}
      >
        <header className="fwtop100-topbar">
          <div className="fwtop100-topbar-inner">
            <div className="fwtop100-brand">
              <Link href="/" aria-label="Go to home page">
                <span className="fwtop100-logo fwtop100-logo--cnbc" />
              </Link>
              <span className="fwtop100-brand-divider" />
              <span className="fwtop100-logo fwtop100-logo--top100" />
            </div>
            <nav className="fwtop100-nav">
              <button type="button" onClick={() => scrollToSection('fwtop100-about')}>
                {content.nav.about}
              </button>
              <button type="button" onClick={() => scrollToSection('fwtop100-methodology')}>
                {content.nav.methodology}
              </button>
              <button type="button" onClick={() => scrollToSection('fwtop100-sectors')}>
                {content.nav.sectors}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isListUnlocked) return;
                  scrollToSection('fwtop100-list');
                }}
                aria-disabled={!isListUnlocked}
              >
                {content.nav.list}
              </button>
              <button type="button">{content.nav.summit}</button>
            </nav>
            <div className="fwtop100-language">
              <button
                type="button"
                className={language === 'en' ? 'active' : ''}
                onClick={() => setLanguage('en')}
              >
                {content.languageToggle.english}
              </button>
              <button
                type="button"
                className={language === 'ar' ? 'active' : ''}
                onClick={() => setLanguage('ar')}
              >
                {content.languageToggle.arabic}
              </button>
            </div>
          </div>
        </header>

        <section className="fwtop100-hero">
          <div className="fwtop100-container">
            <div
              className={`fwtop100-hero-headline ${language === 'ar'
                ? 'fwtop100-hero-headline--rtl'
                : 'fwtop100-hero-headline--ltr'
                }`}
            >
              <div className="fwtop100-hero-mark" />
              <span className="fwtop100-hero-divider" />
              {!isListUnlocked && (
                <div className="fwtop100-countdown fwtop100-countdown--hero">
                  {/* Day-to-go hero lockup temporarily hidden per request. */}
                  {/*
                  <div
                    className={`fwtop100-count-lockup ${
                      language === 'ar'
                        ? 'fwtop100-count-lockup--rtl'
                        : 'fwtop100-count-lockup--ltr'
                    }`}
                    aria-live="polite"
                  >
                    <span className="fwtop100-count-number">{dayDisplay}</span>
                    <div className="fwtop100-count-tags">
                      <span>{language === 'ar' ? 'يوماً' : 'DAYS'}</span>
                      <span>{language === 'ar' ? 'متبقياً' : 'TO GO'}</span>
                    </div>
                  </div>
                  */}
                  <div className="fwtop100-roll-countdown" aria-label="days hours minutes seconds countdown">
                    {renderRollDigits(activeDayDisplay, language === 'ar' ? 'يوماً' : 'DAYS', 0, language === 'ar')}
                    <span className="fwtop100-roll-separator">:</span>
                    {renderRollDigits(activeRollClock.hours, language === 'ar' ? 'ساعة' : 'HRS', 1, language === 'ar')}
                    <span className="fwtop100-roll-separator">:</span>
                    {renderRollDigits(activeRollClock.minutes, language === 'ar' ? 'دقيقة' : 'MIN', 2, language === 'ar')}
                    <span className="fwtop100-roll-separator">:</span>
                    {renderRollDigits(activeRollClock.seconds, language === 'ar' ? 'ثانية' : 'SEC', 3, language === 'ar')}
                  </div>
                </div>
              )}
            </div>
            <p className="fwtop100-eyebrow">{content.hero.eyebrow}</p>
            <h1>{renderAccentText(content.hero.title)}</h1>
            <p className="fwtop100-subtitle">{content.hero.subtitle}</p>
            {isListUnlocked && (
              <button
                type="button"
                className="fwtop100-cta"
                onClick={() => scrollToSection('fwtop100-list')}
              >
                {content.hero.cta}
              </button>
            )}
            <div className="fwtop100-stat-grid" ref={heroStatsRef}>
              {content.hero.stats.map((item) => (
                <div className="fwtop100-stat-item" key={`${item.label}-${item.value}`}>
                  <strong>
                    <ReelInlineValue value={item.value} rtl={language === 'ar'} trigger={heroStatsPulse} />
                  </strong>
                  <p>{item.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="fwtop100-marquee">
            <div className="fwtop100-marquee-track">
              {Array.from({ length: MARQUEE_REPEAT_COUNT }, (_, setIndex) => (
                <div
                  key={`marquee-set-${setIndex}`}
                  className="fwtop100-marquee-set"
                  aria-hidden={setIndex === 0 ? undefined : true}
                >
                  {content.hero.marquee.map((item) => (
                    <span key={`marquee-${setIndex}-${item}`}>{item}</span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="fwtop100-about" className="fwtop100-section">
          <div className="fwtop100-container">
            <p className="fwtop100-label">{content.about.heading}</p>
            <div className="fwtop100-two-col">
              <div>
                <h2>{renderAccentText(content.about.lead)}</h2>
              </div>
              <div>
                {content.about.description.map((paragraph, index) => (
                  <p key={`${paragraph}-${index}`}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="fwtop100-metrics" ref={metricsRef}>
              {content.about.metrics.map((metric) => (
                <div className="fwtop100-metric-item" key={`${metric.label}-${metric.value}`}>
                  <strong>
                    <ReelInlineValue value={metric.value} rtl={language === 'ar'} trigger={metricsPulse} />
                  </strong>
                  <p>{metric.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="fwtop100-methodology" className="fwtop100-section">
          <div className="fwtop100-container">
            <p className="fwtop100-label">{content.methodology.heading}</p>
            <h2>{renderAccentText(content.methodology.lead)}</h2>
            <div className="fwtop100-pillars">
              {content.methodology.pillars.map((pillar) => (
                <article key={pillar.title} className="fwtop100-pillar">
                  <h3>{pillar.title}</h3>
                  <p>{pillar.description}</p>
                </article>
              ))}
            </div>
            <p className="fwtop100-methodology-note">{content.methodology.eligibilityText}</p>
          </div>
        </section>

        <section id="fwtop100-sectors" className="fwtop100-section">
          <div className="fwtop100-container">
            <p className="fwtop100-label">{content.sectors.heading}</p>
            <h2>{renderAccentText(content.sectors.lead)}</h2>
            <div className="fwtop100-sector-grid">
              {content.sectors.items.map((item, index) => (
                <div key={`${item}-${index}`} className="fwtop100-sector-item">
                  <span
                    className={`fwtop100-sector-icon fwtop100-sector-icon--${sectorKeys[index]}`}
                  />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        {isListUnlocked && (
          <section id="fwtop100-list" className="fwtop100-section fwtop100-list-section">
            <div className="fwtop100-container">
              <div className="fwtop100-list-header">
                <div className="fwtop100-list-logo" />
                <div className="fwtop100-list-copy">
                  <p className="fwtop100-label">{content.list.heading}</p>
                  <h3>{renderAccentText(content.list.lead)}</h3>
                  <p className="fwtop100-list-sublead">{content.list.subLead}</p>
                </div>
              </div>
              <div className="fwtop100-filter-row">
                {filterItems.map((item) => {
                  const label = item === 'all' ? content.list.all : item;
                  return (
                    <button
                      type="button"
                      key={item}
                      className={activeSector === item ? 'active' : ''}
                      onClick={() => setActiveSector(item)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="fwtop100-leader-grid">
                {filteredLeaders.map((leader, leaderIndex) => (
                  <button
                    key={leader.id}
                    type="button"
                    className="fwtop100-leader-card"
                    onClick={() => setSelectedLeader(leader)}
                    onMouseEnter={() => warmPopupImage(leader.imageUrl)}
                    onFocus={() => warmPopupImage(leader.imageUrl)}
                  >
                    <div className="fwtop100-leader-media">
                      <div className="fwtop100-leader-avatar">
                        {!!leader.imageUrl && !failedLeaderImages[leader.id] && (
                          <Image
                            className="fwtop100-leader-avatar-image"
                            src={leader.imageUrl}
                            alt={language === 'ar' ? leader.nameArabic : leader.name}
                            layout="fill"
                            quality={50}
                            sizes="(max-width: 640px) 110px, (max-width: 1200px) 150px, 150px"
                            loading={leaderIndex < PRIORITY_IMAGE_COUNT ? 'eager' : 'lazy'}
                            priority={leaderIndex < 4}
                            onError={() => {
                              setFailedLeaderImages((prev) => ({
                                ...prev,
                                [leader.id]: true,
                              }));
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="fwtop100-leader-content">
                      <h3>{language === 'ar' ? leader.nameArabic : leader.name}</h3>
                      <p>
                        {language === 'ar'
                          ? leader.positionArabic || leader.position
                          : leader.position || leader.positionArabic}
                      </p>
                      {!!String(
                        language === 'ar'
                          ? leader.companyArabic || leader.company
                          : leader.company || leader.companyArabic,
                      ).trim() && (
                        <span className="fwtop100-leader-company-pill">
                          {language === 'ar'
                            ? leader.companyArabic || leader.company
                            : leader.company || leader.companyArabic}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {isLeadersLoading && (
                <p className="fwtop100-list-sublead">
                  {language === 'ar' ? 'جار تحميل القادة...' : 'Loading leaders...'}
                </p>
              )}
              {!!leadersError && (
                <p className="fwtop100-list-sublead">
                  {language === 'ar'
                    ? 'تعذّر تحميل البيانات. يتم عرض بيانات تجريبية.'
                    : 'Could not load API data. Showing fallback data.'}
                </p>
              )}
            </div>
          </section>
        )}

        {selectedLeader && (
          <div
            className="fwtop100-modal-backdrop"
            onClick={() => setSelectedLeader(null)}
            role="presentation"
          >
            <div
              className={`fwtop100-modal fwtop100-modal--${popupImageFit}`}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <button
                type="button"
                className="fwtop100-modal-close"
                onClick={() => setSelectedLeader(null)}
              >
                ×
              </button>
              <div className="fwtop100-modal-layout">
                <div className="fwtop100-modal-media">
                  <div className="fwtop100-popup-avatar">
                    {!!selectedLeader.imageUrl && !isPopupImageFailed && (
                      <Image
                        className="fwtop100-popup-avatar-image"
                        src={selectedLeader.imageUrl}
                        alt={language === 'ar' ? selectedLeader.nameArabic : selectedLeader.name}
                        layout="fill"
                        quality={50}
                        sizes="(max-width: 900px) 100vw, 42vw"
                        loading="eager"
                        unoptimized
                        priority
                        onLoadingComplete={(image) => {
                          const width = image?.naturalWidth || 0;
                          const height = image?.naturalHeight || 0;
                          if (!width || !height) {
                            setPopupImageFit('unknown');
                            return;
                          }
                          const ratio = width / height;
                          if (ratio >= 1.15) {
                            setPopupImageFit('landscape');
                            return;
                          }
                          if (ratio <= 0.98) {
                            setPopupImageFit('portrait');
                            return;
                          }
                          setPopupImageFit('square');
                        }}
                        onError={() => {
                          setIsPopupImageFailed(true);
                          setPopupImageFit('unknown');
                        }}
                      />
                    )}
                  </div>
                </div>
                <div className="fwtop100-modal-content">
                  <p className="fwtop100-label">{content.list.popupTitle}</p>
                  <h3>{language === 'ar' ? selectedLeader.nameArabic : selectedLeader.name}</h3>
                  <p className="fwtop100-modal-role">
                    {language === 'ar' ? selectedLeader.positionArabic : selectedLeader.position}
                  </p>
                  <p className="fwtop100-modal-company">
                    {language === 'ar'
                      ? selectedLeader.companyArabic || selectedLeader.company
                      : selectedLeader.company || selectedLeader.companyArabic}
                  </p>
                  <p className="fwtop100-modal-sector">
                    {language === 'ar' ? selectedLeader.sectorArabic : selectedLeader.sector}
                  </p>
                  <p className="fwtop100-modal-bio">
                    {language === 'ar'
                      ? selectedLeader.biographyArabic
                      : selectedLeader.biography}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default FwTop100Page;
